const DEFAULT_MAX_ATTEMPTS = 4
const DEFAULT_BASE_DELAY_MS = 1000

export const sleep = (ms: number) => new Promise<void>(resolve => {
  setTimeout(resolve, ms)
})

/** True when a retry may help (server/network flake), not for stable 4xx validation errors. */
export function isTransientHttpError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status

  if (status === undefined) {
    return true
  }

  if (status >= 500) {
    return true
  }

  if (status === 408 || status === 429) {
    return true
  }

  return false
}

export type TransientRetryResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: unknown }

function getHttpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status
}

/** Do not waste attempts re-posting the same body when auth is the problem. */
function isNonRetryableAuthError(err: unknown): boolean {
  const status = getHttpStatus(err)

  return status === 401 || status === 403
}

/**
 * Re-runs an async operation with exponential backoff after failures.
 * - Default: only transient errors (network, 5xx, 408, 429).
 * - `retryForSave`: up to `maxAttempts` tries on most failures (still no extra Redux
 *   dispatches / toasts between tries); skips retry for 401/403.
 */
export async function executeWithTransientRetries<T>(
  operation: () => Promise<T>,
  options?: {
    maxAttempts?: number
    baseDelayMs?: number
    /** Product PATCH and similar: retry even on 4xx (e.g. flaky validation) except 401/403. */
    retryForSave?: boolean
  }
): Promise<TransientRetryResult<T>> {
  const maxAttempts = options?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS
  const baseDelayMs = options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS
  const retryForSave = options?.retryForSave === true
  let lastErr: unknown

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      const delayMs = Math.min(baseDelayMs * 2 ** (attempt - 1), 5000)

      await sleep(delayMs)
    }

    try {
      const data = await operation()

      return { ok: true, data }
    } catch (err) {
      lastErr =
        (err as { response?: { data?: unknown } })?.response?.data ??
        (err as Error)?.message ??
        err
      const isLastAttempt = attempt === maxAttempts - 1

      if (isLastAttempt) {
        return { ok: false, error: lastErr }
      }

      if (isNonRetryableAuthError(err)) {
        return { ok: false, error: lastErr }
      }

      const canRetry = retryForSave || isTransientHttpError(err)

      if (!canRetry) {
        return { ok: false, error: lastErr }
      }
    }
  }

  return { ok: false, error: lastErr }
}
